import { useState } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import Button from "../../common/Button";
import Dropdown from "../../Settings/Dropdown";
import SlideDrawer from "../../common/SlideDrawer";
import Reveal from "../../common/Reveal";
import { AgencyTagRow, DOC_TYPE_LABELS, SearchHit, documentsApi } from "./api";
import { TagChip } from "./tagColors";

interface Props {
  open: boolean;
  onClose: () => void;
  tags: AgencyTagRow[];
}

// What the report writer would be handed for a query: the top sections,
// their tags, and a relative score. For checking that a manual was split and
// tagged well enough to be found.
const SearchPreview: React.FC<Props> = ({ open, onClose, tags }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("");
  const [tagId, setTagId] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      setHits(await documentsApi.search(q.trim(), { docType: docType || undefined, tagIds: tagId ? [tagId] : undefined }));
    } finally {
      setBusy(false);
    }
  };
  const max = hits?.[0]?.score || 1;

  return (
    <SlideDrawer open={open} storageKey="pnai-agencyDocSearchWidth" defaultWidth={460} className="border-l border-base-300 bg-base-100">
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-2 px-4 h-12 border-b border-base-300">
          <MagnifyingGlassIcon className="size-5 text-blue-500 dark:text-blue-300" />
          <h3 className="font-semibold text-base-content">Retrieval preview</h3>
          <button className="ml-auto p-1.5 rounded-md hover:bg-base-200 text-base-content/60" onClick={onClose} title="Close">
            <XMarkIcon className="size-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 border-b border-base-300">
          <p className="text-xs text-base-content/60">Type something an officer might dictate. These are the sections the report writer would be given.</p>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              run();
            }}
          >
            <input
              className="input input-bordered input-sm flex-1 bg-base-100 focus:outline-none focus:border-blue-500"
              placeholder="officer deployed taser after verbal commands"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit" variant="primary" size="sm" loading={busy} disabled={!q.trim()}>
              Search
            </Button>
          </form>
          <div className="flex flex-wrap gap-2">
            <Dropdown size="sm" dense menuAlign="start" options={[{ value: "", label: "Any type" }, ...Object.entries(DOC_TYPE_LABELS).map(([value, label]) => ({ value, label }))]} value={docType} onChange={setDocType} />
            <Dropdown size="sm" dense menuAlign="start" options={[{ value: "", label: "Any tag" }, ...tags.map((t) => ({ value: t.id, label: t.name }))]} value={tagId} onChange={setTagId} />
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-2">
          {hits === null ? null : hits.length === 0 ? (
            <p className="text-sm text-base-content/60">Nothing matched. Try other words, or check that the document finished processing.</p>
          ) : (
            hits.map((h, i) => (
              <Reveal key={h.sectionId} index={i} className="rounded-lg border border-base-300 bg-base-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <button className="text-left min-w-0" onClick={() => navigate(`/agency/dashboard/documents/${h.documentId}?section=${h.sectionId}`)}>
                    <p className="text-sm font-medium text-base-content truncate hover:underline">
                      {h.sectionNumber ? `§ ${h.sectionNumber} ` : ""}
                      {h.heading || "Untitled section"}
                    </p>
                    <p className="text-[11px] text-base-content/50 truncate">
                      {h.documentTitle} · {DOC_TYPE_LABELS[h.docType]}
                      {h.pageStart ? ` · p.${h.pageStart}${h.pageEnd && h.pageEnd !== h.pageStart ? `-${h.pageEnd}` : ""}` : ""}
                    </p>
                  </button>
                  <div className="w-16 shrink-0">
                    <div className="h-1.5 rounded-full bg-base-300 overflow-hidden" title={`score ${h.score.toFixed(3)}`}>
                      <div className="h-full bg-blue-500" style={{ width: `${Math.max(8, Math.round((h.score / max) * 100))}%` }} />
                    </div>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-base-content/70 line-clamp-4 whitespace-pre-line">{h.excerpt}</p>
                {h.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {h.tags.map((t) => (
                      <TagChip key={t.id} name={t.name} color={t.color} />
                    ))}
                  </div>
                )}
              </Reveal>
            ))
          )}
        </div>
      </div>
    </SlideDrawer>
  );
};

export default SearchPreview;
